import React, { useState } from "react";
import { Box, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../components";
import CloseIcon from "@mui/icons-material/Close";
import ImageCompressor from "../../utils/ImageCompressor";
import AlertService from "../../service/AlertService";
import UploadIcon from "../../assets/admin/images-icon.svg";
import FirebaseService from "../../service/FirebaseService";
import ApiService from "../../service/ApiService";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import ProjectImage from "../../model/projectImages";
import Project from "../../model/project";
import ToastMessages from "../../model/enum/ToastMessages";
import useStyles from "./styles";
import Misc from "../../utils/Misc";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "88%",
  },
};

interface Props {
  project: Project;
  items: ProjectImage[];
  setItems: (items: ProjectImage[]) => void;
}

const AddImageModal = ({ project, items, setItems }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = async (e: any) => {
    const selectedFiles = Array.from(e.target.files) as File[];

    if (selectedFiles.length > 20) {
      AlertService.warning(ToastMessages.MAX_IMAGES_UPLOAD);
      return;
    }

    for (const file of selectedFiles) {
      if (file.size > 5 * 1024 * 1024) {
        // Compress images
        const compressedFile = await ImageCompressor.compress(file);
        // Replace the original file with the compressed file
        const index = selectedFiles.indexOf(file);
        if (index !== -1 && compressedFile) {
          selectedFiles[index] = compressedFile;
        }
      }
    }

    const fileNamesArray = selectedFiles.map((file: File) => file.name);
    setFileNames(fileNamesArray);
    setFiles(selectedFiles);
  };

  const disableButton = () => {
    return Misc.isArrayEmpty(files);
  };

  const handleUpload = async () => {
    if (!disableButton()) {
      setLoading(true);
      await FirebaseService.uploadFiles(files, `Projects/${project.code}`)
        .then(async (urls: string[]) => {
          if (urls.length > 0) {
            const data = {
              projectId: project.id,
              imageUrls: urls,
            };

            if (items.length > 0) {
              // If project already has images, append the new ones
              data.imageUrls = [
                ...data.imageUrls,
                ...items.map((image: ProjectImage) => image.imageUrl),
              ];
            }

            await ApiService.post<any>(
              `${DatabaseEntities.PROJECTS}/${DatabaseEntities.IMAGES}`,
              data
            )
              .then(() => {
                AlertService.success(ToastMessages.IMAGES_UPLOADED);
              })
              .catch(() => {
                AlertService.error(ToastMessages.IMAGES_ERROR_SERVER);
              });
          }
        })
        .catch((error) => {
          AlertService.error(ToastMessages.IMAGES_ERROR_FIREBASE);
          console.error(`${ToastMessages.IMAGES_ERROR_FIREBASE}:`, error);
        });
      setLoading(false);
      setFileNames([]);
      setFiles([]);
      setItems([]);
      handleClose();
    } else {
      AlertService.warning("Please select files to upload.");
    }
  };

  return (
    <div>
      <Btn className={classes.button} onClick={handleOpen}>
        + Add image
      </Btn>
      {loading && <Spinner />}
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <Typography className={classes.title}>Add Image</Typography>
              <Btn onClick={handleClose}>
                <CloseIcon />
              </Btn>
            </div>
            <hr />
            <div className={classes.uploadContainer}>
              <>
                <div className={classes.uploadInput}>
                  <input
                    type="file"
                    id="add-single-img"
                    accept="image/*"
                    className="d-none"
                    onChange={handleFileChange}
                    multiple
                  />
                  <label id="image-label" htmlFor="add-single-img">
                    <img
                      src={UploadIcon}
                      className={classes.image}
                      alt="upload icon"
                    />
                    <label>Drop files to upload or browse</label>
                  </label>
                </div>
                {files.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    {files.map((file, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: 60,
                          height: 60,
                          border: "1px solid #eee",
                          borderRadius: 4,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "#fafafa",
                        }}
                      >
                        {file.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            title={file.name}
                            style={{ maxWidth: "100%", maxHeight: "100%" }}
                            onLoad={(e) =>
                              URL.revokeObjectURL(
                                (e.target as HTMLImageElement).src
                              )
                            }
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 12,
                              color: "#888",
                              textAlign: "center",
                            }}
                          >
                            {file.name}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            </div>
            <div className={classes.buttonDiv}>
              <Btn
                className={classes.button}
                onClick={() => handleUpload()}
                disabled={disableButton()}
              >
                Upload
              </Btn>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default AddImageModal;
